import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, getUser, API, getToken } from "@/lib/api";
import { Clock, Calendar, Globe, User, PlayCircle, BookOpen, ArrowLeft, CheckCircle2, FileText, Video } from "lucide-react";
import { toast } from "sonner";

export default function CourseDetails() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const user = getUser();
  const nav = useNavigate();

  const load = async () => {
    try {
      const [c, m, a, q, e] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/materials?course_id=${id}`),
        api.get(`/assignments?course_id=${id}`),
        api.get(`/quizzes?course_id=${id}`),
        user?.role === "student" ? api.get("/enrollments/me") : Promise.resolve({ data: [] }),
      ]);
      setCourse(c.data); setMaterials(m.data); setAssignments(a.data);
      setQuizzes(q.data); setEnrollments(e.data);
    } catch (err) { toast.error("Failed to load course"); }
  };
  useEffect(() => { load(); }, [id]);

  const enrolled = enrollments.some((x) => x.course_id === id);

  const enroll = async () => {
    try {
      await api.post(`/enrollments/${id}`);
      toast.success("Enrolled! Check your email for the Zoom link.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Enroll failed"); }
  };

  if (!course) return <Layout title="Loading…" subtitle="Course"><div className="text-slate-500">Loading course…</div></Layout>;

  return (
    <Layout title={course.title} subtitle="Course Details">
      <button onClick={() => nav(-1)} data-testid="course-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="h-56 bg-gradient-to-br from-blue-500 to-violet-500 relative">
            {course.thumbnail && <img src={course.thumbnail} alt="" className="w-full h-full object-cover" />}
            <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-white/90 text-slate-700 px-3 py-1 rounded-full">{course.category} · {course.level}</span>
          </div>
          <div className="p-6">
            <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">{course.title}</h1>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{course.description}</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              <Meta icon={Clock} label="Duration" value={course.duration} />
              <Meta icon={Calendar} label="Timing" value={course.class_timing} />
              <Meta icon={Globe} label="Language" value={course.language} />
              <Meta icon={Calendar} label="Starts" value={course.start_date} />
              <Meta icon={Calendar} label="Ends" value={course.end_date} />
              <Meta icon={User} label="Trainer" value={course.trainer_name} />
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
              <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Materials</h3>
              {materials.length === 0 && <div className="text-sm text-slate-500">No materials yet.</div>}
              <div className="space-y-3">
                {materials.map((m) => (
                  <MaterialItem key={m.id} m={m} />
                ))}
              </div>
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
              <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Assignments & Quizzes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignments.map((a) => (
                  <div key={a.id} data-testid={`assn-${a.id}`} className="border border-slate-200 rounded-xl p-4">
                    <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-500">Due {a.due_date} · {a.max_score} pts</div>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.description}</p>
                  </div>
                ))}
                {quizzes.map((q) => (
                  <div key={q.id} data-testid={`quiz-${q.id}`} className="border border-slate-200 rounded-xl p-4">
                    <div className="text-sm font-semibold text-slate-900">{q.title}</div>
                    <div className="text-xs text-slate-500">{q.questions.length} questions</div>
                  </div>
                ))}
                {assignments.length === 0 && quizzes.length === 0 && <div className="text-sm text-slate-500">Nothing here yet.</div>}
              </div>
            </div>
          </div>
        </div>

        <aside className="bg-white border border-slate-200 rounded-2xl p-6 h-fit sticky top-24">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Enrollment</div>
          <h3 className="font-display font-bold text-xl text-slate-900 mt-1">Get started today</h3>
          <p className="text-sm text-slate-500 mt-2">Instant access · Zoom link on email · Assignments & quiz included.</p>

          {user?.role === "student" && (
            enrolled ? (
              <div className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-800 text-sm flex items-center gap-2" data-testid="already-enrolled">
                <CheckCircle2 className="w-4 h-4" /> You&apos;re enrolled
              </div>
            ) : (
              <button data-testid="enroll-btn" onClick={enroll} className="mt-4 w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
                Enroll now
              </button>
            )
          )}

          <a href={course.zoom_url} target="_blank" rel="noreferrer" data-testid="join-zoom-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-slate-200 text-slate-700 hover:border-blue-500 transition-colors">
            <PlayCircle className="w-4 h-4" /> Join Live Class
          </a>
          <div className="mt-4 text-xs text-slate-500">
            Assignment deadline: <span className="text-slate-800 font-medium">{course.assignment_deadline || course.end_date}</span>
          </div>
        </aside>
      </div>
    </Layout>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-blue-600 mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 font-medium">{value}</div>
      </div>
    </div>
  );
}

function MaterialItem({ m }) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const openPreview = async () => {
    if (m.kind === "link" && m.url) {
      window.open(m.url, "_blank");
      return;
    }
    if (!m.storage_path) return;
    if (blobUrl) { setOpen((o) => !o); return; }
    try {
      const res = await fetch(`${API}/materials/file/${m.storage_path}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const blob = await res.blob();
      setBlobUrl(URL.createObjectURL(blob));
      setOpen(true);
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  const Icon = m.kind === "video" ? Video : m.kind === "note" ? FileText : BookOpen;

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`material-${m.id}`}>
      <div className="flex items-center gap-3 p-3">
        <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="flex-1 text-sm text-slate-800 truncate">{m.title}</div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{m.kind}</span>
        <button data-testid={`preview-${m.id}`} onClick={openPreview} className="text-blue-600 text-sm hover:underline">
          {m.kind === "link" ? "Open" : open ? "Hide" : "Preview"}
        </button>
      </div>
      {open && blobUrl && m.kind === "video" && (
        <video controls src={blobUrl} className="w-full max-h-[400px] bg-black" data-testid={`video-${m.id}`} />
      )}
      {open && blobUrl && (m.kind === "note" || m.content_type?.includes("pdf")) && (
        <iframe title={m.title} src={blobUrl} className="w-full h-[500px] bg-slate-50" data-testid={`pdf-${m.id}`} />
      )}
    </div>
  );
}
